"use client";

import HowItWorks from "./HowItWorks";
import WhoWeAre from "./WhoWeAre";
import FeaturesSection from "./FeaturesSection";
import TechnologiesSection from "./Technologies";
import { motion } from "framer-motion";
import TeamSection from "./TeamSection";
import StatisticsSection from "./StatisticsSection";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

import Icon1Src from "../../assets/icons/icon1-robot-person.svg";
import Icon2Src from "../../assets/icons/icon2-standing-person.svg";
import Icon3Src from "../../assets/icons/icon3-walking-person.svg";
import Lottie from "lottie-react";
import HeroAnimation from "../../assets/animations/heroAnimation.json";

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const homeSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Fiieve",
  "url": "https://fiieve.com/",
  "logo": "https://fiieve.com/Images/logo.png",
  "description": "Fiieve is a UK-based software and engineering company delivering web development, mobile apps, AI automation, UI/UX design, and IT consultancy for startups and enterprises.",
  "foundingDate": "2020",
  "sameAs": [
    "https://twitter.com/fiieve",
    "https://www.linkedin.com/company/fiieve",
    "https://www.instagram.com/fiieve"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "url": "https://fiieve.com/contact-us",
    "availableLanguage": "English"
  },
  "address": { "@type": "PostalAddress", "addressCountry": "GB" },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Software & Engineering Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Web Development" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mobile App Development" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Automation" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "UI/UX Design" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Custom Software Development" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "IoT Development" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Embedded Systems" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Robotics & Automation" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "PCB Design" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Smart Home Systems" } }
    ]
  }
});

// ─── Float animation ──────────────────────────────────────────────────────────
const animationCSS = `
  .icon-float   { animation: iconFloat 3.6s ease-in-out infinite; }
  .icon-float-2 { animation: iconFloat 3.6s ease-in-out infinite; animation-delay: -1.2s; }
  .icon-float-3 { animation: iconFloat 3.6s ease-in-out infinite; animation-delay: -2.4s; }
  @keyframes iconFloat {
    0%, 100% { transform: translateY(0px);  }
    50%       { transform: translateY(-8px); }
  }
`;

export default function HomePage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const bg        = isDark ? "bg-black"      : "bg-[#f0f4f0]";
  const greenText = isDark ? "text-green-400": "text-green-700";
  const text      = isDark ? "text-white"    : "text-gray-900";
  const textMuted = isDark ? "text-gray-300" : "text-gray-600";
  const cardBg    = isDark ? "bg-[#0d0d0d]" : "bg-white";
  const cardBorder= isDark ? "border-white/10 hover:border-green-400/40" : "border-green-500 hover:border-green-600";
  const cardText  = isDark ? "text-white"    : "text-gray-900";
  const cardDesc  = isDark ? "text-gray-400" : "text-gray-600";

  return (
    <>
      {/* ── SEO Head ── */}
      <SEOHead
        title="Web Development & AI Automation Company UK | Fiieve"
        description="Fiieve builds high-performance websites, mobile apps & AI automation systems for UK startups and enterprises. Expert web developers & AI engineers. Get a free quote today."
        canonical="https://fiieve.com/"
        ogImage="https://fiieve.com/Images/og-home.jpg"
        schema={homeSchema}
      />

      <div
        className={`min-h-screen ${bg} ${text} overflow-hidden relative pt-16 sm:pt-20 lg:pt-24 transition-colors duration-300`}
        style={{
          backgroundImage: `
            linear-gradient(${isDark ? "rgba(74,222,128,0.04)" : "rgba(74,222,128,0.07)"} 1px, transparent 1px),
            linear-gradient(90deg, ${isDark ? "rgba(74,222,128,0.04)" : "rgba(74,222,128,0.07)"} 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      >
        <style>{animationCSS}</style>

        {/* ===== HERO ===== */}
        <section aria-label="Hero" className="container mx-auto px-6 sm:px-10 lg:px-16 py-6 lg:py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-10">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="w-full lg:w-1/2 space-y-6 text-center lg:text-left z-30"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.2rem] font-extrabold leading-[1.08] tracking-tight">
                <span className={text}>We Build the </span>
                <span className={greenText}>Technology </span>
                <span className="text-orange-400">That Powers Tomorrow.</span>
              </h1>

              <p className={`${textMuted} text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0`}>
                Fiieve is a UK-based software &amp; engineering company — delivering world-class
                <strong> web development</strong>, <strong>mobile apps</strong>,{" "}
                <strong>AI automation</strong>, and <strong>IT consultancy</strong> for
                startups and enterprises.
              </p>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-1">
                <button
                  onClick={() => navigate("/contact-us")}
                  aria-label="Start a project with Fiieve"
                  className="inline-flex items-center gap-2 bg-green-400 text-black font-bold px-6 py-2.5 rounded-full text-sm hover:bg-green-300 transition-all duration-300 hover:scale-105 shadow-[0_0_25px_rgba(74,222,128,0.45),0_0_35px_rgba(255,165,0,0.25)]"
                >
                  Start a Project
                </button>
                <button
                  onClick={() => navigate("/portfolio")}
                  aria-label="View Fiieve's portfolio"
                  className={`inline-flex items-center gap-2 border ${
                    isDark ? "border-white/30 text-white" : "border-gray-400 text-gray-700"
                  } font-bold px-6 py-2.5 rounded-full text-sm hover:border-green-400 hover:text-green-400 transition-all duration-300 hover:scale-105 shadow-[0_0_25px_rgba(74,222,128,0.45),0_0_35px_rgba(255,165,0,0.25)]`}
                >
                  View Our Work
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              className="w-full lg:w-1/2 flex items-center justify-center relative"
            >
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[70%] h-[70%] bg-green-400/10 rounded-full blur-[80px]" />
              </div>
              <div className="relative w-full max-w-xl icon-float" style={{ filter: "drop-shadow(0 8px 32px rgba(74,222,128,0.10))" }}>
                <Lottie animationData={HeroAnimation} loop={true} style={{ width: "100%", height: "100%" }} aria-label="Fiieve technology animation" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ===== 3 TOP FEATURE CARDS ===== */}
        <section aria-label="Featured Services" className="container mx-auto px-6 sm:px-10 lg:px-16 pb-6 mt-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-5"
          >
            {[Icon1Src, Icon2Src, Icon3Src].map((icon, i) => {
              const cardData = [
                { title: "Mobile App Development", desc: "We create powerful mobile apps with seamless UX and smooth performance for Android and iOS.", path: "/mobile-app-development" },
                { title: "AI Agent & Automation", desc: "Leverage AI agents and automation to optimise workflows, eliminate repetition and lead your market.", path: "/ai-automation" },
                { title: "Web Development",        desc: "We build responsive, high-performance web apps that showcase your brand and engage users.", path: "/web-development" },
              ][i];

              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.15 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(cardData.path)}
                  className={`group relative ${cardBg} border ${cardBorder} rounded-2xl px-5 pt-4 pb-0 flex flex-row items-end justify-between gap-4 overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(74,222,128,0.35),0_0_20px_rgba(255,165,0,0.2)] hover:-translate-y-1 cursor-pointer`}
                  style={{ minHeight: "140px" }}
                  role="link"
                  aria-label={`Learn more about ${cardData.title}`}
                >
                  <div className="flex flex-col gap-2 z-10 flex-1 pb-4 self-end">
                    <div>
                      <h2 className={`${cardText} font-bold text-base mb-1`}>{cardData.title}</h2>
                      <p className={`${cardDesc} text-xs leading-relaxed`}>{cardData.desc}</p>
                    </div>
                    <div className={`w-8 h-8 rounded-full border ${isDark ? "border-white/20 text-white" : "border-gray-700 text-gray-900"} group-hover:border-green-400 group-hover:bg-green-400 group-hover:text-black flex items-center justify-center text-sm font-bold transition-all duration-300 mt-1 select-none`}>
                      &#8594;
                    </div>
                  </div>
                  <div className="flex-shrink-0 self-end pointer-events-none" style={{ lineHeight: 0, transform: i === 1 ? "translateY(10px)" : "none" }}>
                    <img src={icon} alt={`${cardData.title} icon`} width={110} height={130} loading="lazy" style={{ display: "block", verticalAlign: "bottom" }} />
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </section>

        {/* ===== WHAT WE DO — SOFTWARE SERVICES ===== */}
        <section
          aria-labelledby="home-services-heading"
          className={`${bg} ${text} py-4 px-4 sm:px-10 lg:px-10 mt-4 sm:mt-8 transition-colors duration-300`}
          style={{
            backgroundImage: `linear-gradient(${isDark ? "rgba(74,222,128,0.04)" : "rgba(74,222,128,0.07)"} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? "rgba(74,222,128,0.04)" : "rgba(74,222,128,0.07)"} 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        >
          <div className="text-center mb-4">
            <h2 id="home-services-heading" className={`text-xl sm:text-2xl lg:text-3xl font-semibold ${text} mb-2`}>
              Our Software &amp; Engineering Services
            </h2>
          </div>
          <div className="max-w-5xl mx-auto text-center mb-10">
            <h3 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${greenText} mb-4`}>
              Build Trust and Introduce Your Mission
            </h3>
            <p className={`${textMuted} text-sm sm:text-base lg:text-lg max-w-3xl mx-auto`}>
              We&apos;re on a mission to create products that change the way people live, work and play.
            </p>
          </div>
          <ServicesSection isDark={isDark} greenText={greenText} />
        </section>

        {/* ===== LOWER SECTIONS ===== */}
        {[WhoWeAre, HowItWorks, FeaturesSection, TechnologiesSection, TeamSection, StatisticsSection].map(
          (Component, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: i === 0 ? 0 : 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <Component />
            </motion.div>
          )
        )}
      </div>
    </>
  );
}

// ─── Services Grid (Software + Engineering) ───────────────────────────────────
function ServicesSection({ isDark, greenText = "text-green-400" }) {
  const navigate = useNavigate();

  const cardFrom     = isDark ? "from-[#0d0d0d]" : "from-[#1a2e1a]";
  const cardTo       = isDark ? "to-[#1a1a1a]"   : "to-[#0f1f0f]";
  const descColor    = "text-gray-300";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const bodyColor    = isDark ? "text-gray-300" : "text-gray-600";

  const softwareServices = [
    {
      id: 1, title: "Mobile App Development", heading: "MOBILE APP DEVELOPMENT",
      img: "/Images/mobileapp.png", path: "/mobile-app-development",
      description: "Powerful mobile apps with seamless UX and smooth performance for both Android and iOS platforms.",
    },
    {
      id: 2, title: "Web Development", heading: "WEB DEVELOPMENT",
      img: "/Images/webdev.png", path: "/web-development",
      description: "Responsive, high-performance web apps that showcase your brand and engage your users effectively.",
    },
    {
      id: 3, title: "Graphic & UI/UX Design", heading: "GRAPHIC & UI/UX DESIGN",
      img: "/Images/uiux.png", path: "/ui-ux-design",
      description: "Stunning visuals and intuitive interfaces that delight users and elevate your brand identity.",
    },
    {
      id: 4, title: "Custom Software Development", heading: "CUSTOM SOFTWARE DEVELOPMENT",
      img: "/Images/consultancy.png", path: "/custom-software-development",
      description: "Bespoke software built from scratch — tailored to your exact requirements and scalable from day one.",
    },
    {
      id: 5, title: "AI Agent & Automation", heading: "AI AGENT & AUTOMATION",
      img: "/Images/ai.png", path: "/ai-automation",
      description: "AI agents and automation workflows that eliminate repetitive tasks and optimise your business operations.",
    },
  ];

  const engineeringServices = [
    {
      id: 6, title: "IoT Development", heading: "IOT DEVELOPMENT",
      img: "/Images/iot.png", path: "/engineering/iot",
      description: "End-to-end IoT systems — from sensor nodes and firmware to cloud dashboards and mobile control interfaces.",
    },
    {
      id: 7, title: "Embedded Systems", heading: "EMBEDDED SYSTEMS",
      img: "/Images/embedded.png", path: "/engineering/embedded-systems",
      description: "Real-time firmware for ARM Cortex, STM32, AVR, and RISC-V platforms using bare-metal C/C++ and FreeRTOS.",
    },
    {
      id: 8, title: "Robotics & Automation", heading: "ROBOTICS & AUTOMATION",
      img: "/Images/robotics.png", path: "/engineering/robotics",
      description: "Autonomous robotic systems for research, industrial, and educational applications built on ROS2.",
    },
    {
      id: 9, title: "PCB Design", heading: "PCB DESIGN",
      img: "/Images/pcb.png", path: "/engineering/pcb-design",
      description: "Professional PCB design from schematic to Gerber — single-layer to HDI boards optimised for manufacturing.",
    },
    {
      id: 10, title: "Smart Home Systems", heading: "SMART HOME SYSTEMS",
      img: "/Images/smarthome.png", path: "/engineering/smart-home",
      description: "Custom smart home automation — lighting, climate, security, and whole-home hubs with Home Assistant.",
    },
  ];

  const cardClasses = `bg-gradient-to-b ${cardFrom} ${cardTo} border border-green-400/40 shadow-[0_0_25px_rgba(16,185,129,0.15)] hover:shadow-[0_0_35px_rgba(16,185,129,0.4),0_0_25px_rgba(255,165,0,0.25)] rounded-2xl p-5 flex flex-col items-center text-center hover:-translate-y-6 hover:scale-105 transition-all duration-300 min-h-[320px] cursor-pointer`;

  const ServiceCard = ({ service, index }) => (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut", delay: index * 0.12 }}
      viewport={{ once: true, amount: 0.3 }}
      onClick={() => navigate(service.path)}
      className={cardClasses}
      role="link"
      aria-label={`Learn more about ${service.title}`}
    >
      <img
        src={service.img}
        alt={`${service.title} service`}
        className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-4 flex-shrink-0"
        loading="lazy"
        width={96}
        height={96}
      />
      <h3 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight flex-shrink-0">
        {service.heading}
      </h3>
      <p className={`${descColor} text-xs sm:text-sm leading-relaxed flex-1`}>
        {service.description}
      </p>
      <button
        onClick={(e) => { e.stopPropagation(); navigate(service.path); }}
        aria-label={`Learn more about ${service.title}`}
        className="mt-5 border-2 border-green-400 text-green-400 px-5 py-2 rounded-full font-medium text-sm transition-all duration-300 hover:bg-green-400 hover:text-black flex-shrink-0"
      >
        Learn More
      </button>
    </motion.article>
  );

  return (
    <>
      {/* ── Software Services ── */}
      <section
        aria-label="Software Services"
        className="w-full pb-6 sm:pb-8 px-4 sm:px-10 lg:px-20 -mt-2 transition-colors duration-300"
      >
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.2 }}
          className="max-w-full mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
        >
          {softwareServices.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </motion.div>
      </section>

      {/* ── Engineering Services ── */}
      <section
        aria-labelledby="home-engineering-heading"
        className="w-full pb-10 sm:pb-12 px-4 sm:px-10 lg:px-20 mt-8 transition-colors duration-300"
      >
        <div className="text-center mb-8">
          <p className={`text-sm font-semibold uppercase tracking-widest ${greenText} mb-1`}>
            Hardware & Embedded
          </p>
          <h2
            id="home-engineering-heading"
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${headingColor} mb-2`}
          >
            Engineering Solutions
          </h2>
          <p className={`${bodyColor} max-w-xl mx-auto text-sm md:text-base`}>
            IoT, embedded systems, robotics, PCB design, and smart home — hardware meets software.
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-full mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
        >
          {engineeringServices.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </motion.div>
      </section>
    </>
  );
}